import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import { isSyncEnabled } from "./useProgressSync";

const HealthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';
const HP_KEY = 'fruitCrush_hp';
const LAST_DAMAGE_KEY = 'fruitCrush_lastDamage';
const UPGRADES_KEY = 'fruitCrush_permanentUpgrades';
const PLAYER_LEVEL_KEY = 'fruitCrush_playerLevel';
const PRESTIGE_KEY = 'fruitCrush_prestige';

// HP Constants
const BASE_MAX_HP = 100;
const HP_PER_DAMAGE = 20;
const HP_BONUS_UPGRADE = 20;      // hp_boost
const HP_BONUS_RESERVOIR = 25;    // hp_reservoir
const HP_BONUS_LEVEL_50 = 20;
const HP_BONUS_LEVEL_100 = 20;
const HP_BONUS_PRESTIGE = 20;
const HP_BONUS_PREMIUM = 500;     // Premium membership bonus

// Key for premium membership health bonus
const PREMIUM_HEALTH_KEY = 'fruitCrush_premiumHealthBonus';

// Regeneration constants
const BASE_HP_PER_CYCLE = 20; // Base: regenerate 20 HP
const BASE_CYCLE_TIME = 10 * 60 * 1000; // Base: 10 minutes for 20 HP
const FAST_REGEN_CYCLE_TIME = 5 * 60 * 1000; // With faster_regen: 5 minutes for 20 HP

// Get player level from localStorage
const getPlayerLevel = () => {
  try {
    const level = localStorage.getItem(PLAYER_LEVEL_KEY);
    return level ? parseInt(level, 10) : 1;
  } catch {
    return 1;
  }
};

// Get prestige from localStorage
const getPrestige = () => {
  try {
    const prestige = localStorage.getItem(PRESTIGE_KEY);
    return prestige ? parseInt(prestige, 10) : 0;
  } catch {
    return 0;
  }
};

// Get premium health bonus from localStorage
const getPremiumHealthBonus = () => {
  try {
    const bonus = localStorage.getItem(PREMIUM_HEALTH_KEY);
    return bonus ? parseInt(bonus, 10) : 0;
  } catch {
    return 0;
  }
};

// Get upgrades from localStorage
const getUpgrades = () => {
  try {
    return JSON.parse(localStorage.getItem(UPGRADES_KEY) || '[]');
  } catch {
    return [];
  }
};

// Calculate max HP based on upgrades, level, and prestige
const calculateMaxHP = () => {
  let maxHP = BASE_MAX_HP;
  const upgrades = getUpgrades();
  const playerLevel = getPlayerLevel();
  const prestige = getPrestige();
  
  // Shop upgrade bonus: hp_boost (+20 HP)
  if (upgrades.includes('hp_boost')) {
    maxHP += HP_BONUS_UPGRADE;
  }
  
  // Shop upgrade bonus: hp_reservoir (+25 HP)
  if (upgrades.includes('hp_reservoir')) {
    maxHP += HP_BONUS_RESERVOIR;
  }
  
  // Level 50 bonus (+20 HP)
  if (playerLevel >= 50) {
    maxHP += HP_BONUS_LEVEL_50;
  }
  
  // Level 100 bonus (+20 HP)
  if (playerLevel >= 100) {
    maxHP += HP_BONUS_LEVEL_100;
  }
  
  // Prestige bonus (+20 HP per prestige, max 1 for now)
  if (prestige >= 1) {
    maxHP += HP_BONUS_PRESTIGE;
  }
  
  // Premium membership bonus (+500 HP)
  const premiumBonus = getPremiumHealthBonus();
  if (premiumBonus > 0) {
    maxHP += premiumBonus;
  }
  
  return maxHP;
};

// Calculate time per 1 HP regeneration in milliseconds
const getTimePerHP = () => {
  const upgrades = getUpgrades();
  
  // Base: 10 minutes for 20 HP = 30 seconds per HP
  let cycleTime = BASE_CYCLE_TIME;
  
  // faster_regen: 5 minutes for 20 HP = 15 seconds per HP
  if (upgrades.includes('faster_regen')) {
    cycleTime = FAST_REGEN_CYCLE_TIME;
  }
  
  // quick_recovery: 50% faster (stacks with faster_regen)
  if (upgrades.includes('quick_recovery')) {
    cycleTime = Math.floor(cycleTime * 0.5);
  }
  
  // Time per 1 HP = cycle time / 20 HP
  return Math.floor(cycleTime / BASE_HP_PER_CYCLE);
};

// Get damage taken per fail based on upgrades
const getDamagePerFail = () => {
  const upgrades = getUpgrades();
  let damage = HP_PER_DAMAGE; // Default is 20
  
  // thick_skin: Reduce damage by 2 (lose 18 instead of 20)
  if (upgrades.includes('thick_skin')) {
    damage -= 2;
  }
  
  return damage;
};

// Get HP data from localStorage
const getHPData = () => {
  const hp = localStorage.getItem(HP_KEY);
  const lastDamage = localStorage.getItem(LAST_DAMAGE_KEY);
  const maxHP = calculateMaxHP();
  return {
    hp: hp !== null ? Math.min(parseInt(hp, 10), maxHP) : maxHP,
    lastDamage: lastDamage ? parseInt(lastDamage, 10) : null
  };
};

// Save HP to localStorage
const saveHPData = (hp, lastDamage) => {
  localStorage.setItem(HP_KEY, hp.toString());
  if (lastDamage) {
    localStorage.setItem(LAST_DAMAGE_KEY, lastDamage.toString());
  }
};

// Sync HP to backend
const syncHPToBackend = async (hp, lastDamage) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;

  try {
    await fetch(`${API_URL}/api/progress`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        hp,
        last_damage: lastDamage ? new Date(lastDamage).toISOString() : null
      })
    });
  } catch (error) {
    console.error('Failed to sync HP:', error);
  }
};

export function HealthProvider({ children }) {
  const [maxHP, setMaxHP] = useState(calculateMaxHP);
  const [timePerHP, setTimePerHP] = useState(getTimePerHP);
  const [hp, setHP] = useState(() => {
    const data = getHPData();
    return data.hp;
  });
  const [lastDamage, setLastDamage] = useState(() => {
    const data = getHPData();
    return data.lastDamage;
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationType, setAnimationType] = useState(null);
  const lastSyncRef = useRef(Date.now());

  // Update max HP and regen time when upgrades/level/prestige change
  useEffect(() => {
    const checkUpdates = () => {
      const newMaxHP = calculateMaxHP();
      const newTimePerHP = getTimePerHP();
      
      if (newMaxHP !== maxHP) {
        const oldMaxHP = maxHP;
        setMaxHP(newMaxHP);
        // If HP was at or above old max, update to new max (grant full HP on upgrade)
        if (hp >= oldMaxHP) {
          setHP(newMaxHP);
          saveHPData(newMaxHP, lastDamage);
        }
      }
      if (newTimePerHP !== timePerHP) setTimePerHP(newTimePerHP);
    };
    
    window.addEventListener('storage', checkUpdates);
    const interval = setInterval(checkUpdates, 2000);
    
    return () => {
      window.removeEventListener('storage', checkUpdates);
      clearInterval(interval);
    };
  }, [maxHP, timePerHP, hp, lastDamage]);

  // Real-time HP regeneration - increment HP one by one
  useEffect(() => {
    if (hp >= maxHP) {
      return; // No need to regenerate if at max
    }

    if (!lastDamage) {
      return; // No damage taken yet
    }

    const regenInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceDamage = now - lastDamage;
      
      // Calculate how many HP should have regenerated by now
      const totalHPToRegen = Math.floor(timeSinceDamage / timePerHP);
      
      setHP(currentHP => {
        if (currentHP >= maxHP) {
          clearInterval(regenInterval);
          return maxHP;
        }
        
        // Calculate actual HP based on time since damage
        const data = getHPData();
        const baseHP = data.hp; // HP when damage was taken
        
        // Regen 1 HP at a time for smooth animation
        const targetHP = Math.min(baseHP + totalHPToRegen, maxHP);
        
        if (currentHP < targetHP) {
          const newHP = currentHP + 1;
          
          // Save to localStorage periodically (not every HP)
          if (now - lastSyncRef.current > 5000) {
            saveHPData(newHP, lastDamage);
            lastSyncRef.current = now;
          }
          
          // Sync to backend every 30 seconds
          if (now - lastSyncRef.current > 30000 && isSyncEnabled()) {
            syncHPToBackend(newHP, lastDamage);
          }
          
          // If reached max, clear the damage timestamp
          if (newHP >= maxHP) {
            setLastDamage(null);
            localStorage.removeItem(LAST_DAMAGE_KEY);
            saveHPData(maxHP, null);
            if (isSyncEnabled()) {
              syncHPToBackend(maxHP, null);
            }
          }
          
          return newHP;
        }
        
        return currentHP;
      });
    }, 1000); // Check every second for smooth updates

    return () => clearInterval(regenInterval);
  }, [hp, lastDamage, maxHP, timePerHP]);

  // Calculate initial HP from time passed (on mount and when lastDamage changes)
  useEffect(() => {
    if (!lastDamage || hp >= maxHP) return;
    
    const now = Date.now();
    const timeSinceDamage = now - lastDamage;
    const hpToRegen = Math.floor(timeSinceDamage / timePerHP);
    
    if (hpToRegen > 0) {
      const data = getHPData();
      const newHP = Math.min(data.hp + hpToRegen, maxHP);
      
      if (newHP > hp) {
        setHP(newHP);
        saveHPData(newHP, lastDamage);
        
        if (newHP >= maxHP) {
          setLastDamage(null);
          localStorage.removeItem(LAST_DAMAGE_KEY);
        }
      }
    }
  }, [lastDamage]);

  // Take damage (lose a level)
  const takeDamage = useCallback(() => {
    if (hp > 0) {
      const damageAmount = getDamagePerFail();
      let newHP = Math.max(0, hp - damageAmount);
      const now = Date.now();
      
      // Check for Second Wind upgrade
      const upgrades = getUpgrades();
      if (newHP < 20 && newHP > 0 && upgrades.includes('second_wind')) {
        const lastUsed = localStorage.getItem('fruitCrush_secondWindUsed');
        let canUseSecondWind = true;
        
        if (lastUsed) {
          const lastUsedDate = new Date(lastUsed).toDateString();
          const today = new Date().toDateString();
          canUseSecondWind = lastUsedDate !== today;
        }
        
        if (canUseSecondWind) {
          newHP = Math.min(newHP + 30, calculateMaxHP());
          localStorage.setItem('fruitCrush_secondWindUsed', new Date().toISOString());
        }
      }
      
      // Trigger damage animation
      setIsAnimating(true);
      setAnimationType('damage');
      setTimeout(() => {
        setIsAnimating(false);
        setAnimationType(null);
      }, 500);
      
      setHP(newHP);
      setLastDamage(now);
      saveHPData(newHP, now);
      
      if (isSyncEnabled()) {
        syncHPToBackend(newHP, now);
      }
      
      return true;
    }
    return false;
  }, [hp]);

  // Heal HP (for rewards)
  const healHP = useCallback((amount = BASE_HP_PER_CYCLE) => {
    if (hp < maxHP) {
      const newHP = Math.min(hp + amount, maxHP);
      
      setIsAnimating(true);
      setAnimationType('heal');
      setTimeout(() => {
        setIsAnimating(false);
        setAnimationType(null);
      }, 500);
      
      setHP(newHP);
      saveHPData(newHP, lastDamage);
      
      if (isSyncEnabled()) {
        syncHPToBackend(newHP, lastDamage);
      }
    }
  }, [hp, lastDamage, maxHP]);

  // Reset HP to full
  const resetHP = useCallback(() => {
    setHP(maxHP);
    setLastDamage(null);
    localStorage.removeItem(LAST_DAMAGE_KEY);
    saveHPData(maxHP, null);
    
    if (isSyncEnabled()) {
      syncHPToBackend(maxHP, null);
    }
  }, [maxHP]);

  // Restore HP to max
  const restoreToMax = useCallback(() => {
    setHP(maxHP);
    setLastDamage(null);
    localStorage.removeItem(LAST_DAMAGE_KEY);
    saveHPData(maxHP, null);
    
    setIsAnimating(true);
    setAnimationType('heal');
    setTimeout(() => {
      setIsAnimating(false);
      setAnimationType(null);
    }, 500);
    
    if (isSyncEnabled()) {
      syncHPToBackend(maxHP, null);
    }
  }, [maxHP]);

  // Calculate HP percentage for progress bar
  const hpPercentage = Math.round((hp / maxHP) * 100);
  
  const canPlay = hp >= HP_PER_DAMAGE;

  // Calculate regen info for display
  const getRegenInfo = useCallback(() => {
    const upgrades = getUpgrades();
    let secondsPerHP = Math.floor(timePerHP / 1000);
    let description = `+1 HP every ${secondsPerHP}s`;
    
    if (upgrades.includes('faster_regen') && upgrades.includes('quick_recovery')) {
      description = '+1 HP every 7.5s (Fast + 50%)';
    } else if (upgrades.includes('quick_recovery')) {
      description = '+1 HP every 15s (50% faster)';
    } else if (upgrades.includes('faster_regen')) {
      description = '+1 HP every 15s (Fast Regen)';
    } else {
      description = '+1 HP every 30s';
    }
    
    return description;
  }, [timePerHP]);

  return (
    <HealthContext.Provider value={{
      hp,
      maxHP,
      hpPercentage,
      canPlay,
      takeDamage,
      healHP,
      resetHP,
      restoreToMax,
      timePerHP,
      isAnimating,
      animationType,
      getRegenInfo,
      HP_PER_DAMAGE,
      HP_PER_REGEN: BASE_HP_PER_CYCLE,
    }}>
      {children}
    </HealthContext.Provider>
  );
}

export function useHealth() {
  const context = useContext(HealthContext);
  if (!context) {
    throw new Error("useHealth must be used within HealthProvider");
  }
  return context;
}

// Keep useLives as an alias for backwards compatibility
export const LivesProvider = HealthProvider;
export const useLives = useHealth;

// Function to set premium health bonus (called when membership status is loaded)
export const setPremiumHealthBonus = (bonus) => {
  localStorage.setItem(PREMIUM_HEALTH_KEY, bonus.toString());
  // Dispatch storage event to trigger immediate health recalculation
  window.dispatchEvent(new Event('storage'));
};

// Function to clear premium health bonus (when membership expires)
export const clearPremiumHealthBonus = () => {
  localStorage.removeItem(PREMIUM_HEALTH_KEY);
};

// Export calculateMaxHP for external use
export { calculateMaxHP };
